import { CommonModule } from '@angular/common';
import {
    Component,
    Input,
    OnInit,
    OnChanges,
    SimpleChanges,
    ChangeDetectionStrategy,
    ChangeDetectorRef
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ContactInfo } from 'src/app/recruiting/deals/models/contact-info.model';
import { SkeletonLoaderDirective } from 'src/app/shared/components/skeleton-loader/skeleton-loader.directive';
import { SharedOffersService } from 'src/app/shared/offers-shared/offers.service';
import {
    OfferTeamContactDto,
    ROLE_DISPLAY_CONFIG,
    CONTACT_QA_IDS,
    ROLE_DISPLAY_CONFIG_WFD,
    CONTACT_QA_IDS_WFD
} from 'src/app/shared/models/offer-team-contact.model';
import { Owner } from 'src/app/shared/helpers/owner.decorator';
import { PhonePipe } from 'src/app/shared/pipes/phone.pipe';
import { LDFeatureManager } from 'src/app/shared/services/ld-feature-manager';
import { FeatureFlag } from 'src/app/shared/models/enums/feature-flag';

@Owner('CodeBusters')
@UntilDestroy()
@Component({
    selector: 'nova-points-of-contact',
    templateUrl: './points-of-contact.component.html',
    styleUrls: ['./points-of-contact.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [CommonModule, MatIconModule, MatCardModule, SkeletonLoaderDirective, PhonePipe]
})
export class PointsOfContactComponent implements OnInit, OnChanges {
    @Input() offerId?: number;
    @Input() title = 'Team Info';

    readonly skeletonRowCount = 7;
    readonly contactQaIds = CONTACT_QA_IDS;

    readonly skeletonRowCountWFD = 8;
    readonly contactQaIdsWFD = CONTACT_QA_IDS_WFD;

    contacts: ContactInfo[] = [];
    loading = true;
    error = false;
    ffDisplayWfdRecruiterDeals = this._featureManager.isEnabledSignal(FeatureFlag.WFD_5491_Deals_Display_Wfd_Recruiter);

    private lastOfferId: number | null = null;

    constructor(
        private readonly sharedOffersService: SharedOffersService,
        private readonly cdr: ChangeDetectorRef,
        private readonly _featureManager: LDFeatureManager
    ) {}

    ngOnInit(): void {
        this.validateInputsAndLoad();
    }

    ngOnChanges(changes: SimpleChanges): void {
        const offerIdChanged = changes.offerId && !changes.offerId.firstChange;

        if (offerIdChanged) {
            const offerId = changes.offerId.currentValue as number | null | undefined;

            if (offerId !== this.lastOfferId) {
                this.validateInputsAndLoad();
            }
        }
    }

    hasValidContactName(contact: ContactInfo): boolean {
        return !!contact.name;
    }

    isFallbackPhone(contact: ContactInfo): boolean {
        return !contact.phone && this.hasValidContactName(contact);
    }

    private validateInputsAndLoad(): void {
        if (this.offerId == null) {
            this.loading = false;
            this.error = true;
            this.contacts = [];
            this.cdr.markForCheck();
            return;
        }

        this.loadContacts();
    }

    private loadContacts(): void {
        if (this.offerId == null) {
            return;
        }

        const offerId = this.offerId;

        this.lastOfferId = offerId;
        this.loading = true;
        this.error = false;

        this.sharedOffersService
            .getOfferTeamContacts(offerId)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (teamContacts) => {
                    if (this.ffDisplayWfdRecruiterDeals()) {
                        const sortedContacts = this.sortContactsByRole(teamContacts);
                        this.contacts = this.mapContactsWFD(sortedContacts);
                    } else {
                        this.contacts = this.mapContacts(teamContacts);
                    }

                    this.loading = false;
                    this.error = false;
                    this.cdr.markForCheck();
                },
                error: () => {
                    this.handleLoadError();
                }
            });
    }

    private sortContactsByRole(teamContacts: OfferTeamContactDto[]): OfferTeamContactDto[] {
        const roleOrder = ROLE_DISPLAY_CONFIG_WFD.map((config) => config.role);

        return teamContacts.sort((a, b) => {
            const indexA = roleOrder.indexOf(a.role);
            const indexB = roleOrder.indexOf(b.role);

            if (indexA === -1 && indexB === -1) {
                return 0;
            }
            if (indexA === -1) {
                return 1;
            }
            if (indexB === -1) {
                return -1;
            }

            return indexA - indexB;
        });
    }

    private mapContactsWFD(teamContacts: OfferTeamContactDto[] = []): ContactInfo[] {
        return teamContacts.map((contact) => {
            const config = ROLE_DISPLAY_CONFIG_WFD.find((r) => r.role === contact.role);
            const label = config?.label ?? contact.role;
            const source = config?.source ?? 'facility';

            return this.createContact(
                label,
                contact.name ?? undefined,
                contact.email ?? undefined,
                contact.phone ?? undefined,
                source
            );
        });
    }

    private mapContacts(teamContacts: OfferTeamContactDto[] = []): ContactInfo[] {
        return teamContacts.map((contact) => {
            const config = ROLE_DISPLAY_CONFIG.find((r) => r.role === contact.role);
            const label = config?.label ?? contact.role;
            const source = config?.source ?? 'facility';

            return this.createContact(
                label,
                contact.name ?? undefined,
                contact.email ?? undefined,
                contact.phone ?? undefined,
                source
            );
        });
    }

    private handleLoadError(): void {
        this.contacts = [];
        this.error = true;
        this.loading = false;
        this.cdr.markForCheck();
    }

    private createContact(
        label: string,
        name: string | undefined,
        email: string | undefined,
        phone: string | undefined,
        source: 'candidate' | 'facility'
    ): ContactInfo {
        return {
            label,
            name,
            email,
            phone,
            source
        };
    }
}
